import { styled } from '@linaria/react';
import { Box, Group, SimpleGrid, Stack, Tooltip } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';
import type { AgentAdoptionResponse } from '../../../api/agent-miner-client/agent-miner-client.type';
import { ChartBox } from '../../../components/chart-styles/chart-styles';
import { SingleValueCard } from '../../../components/overview-card/single-value-card';
import { inkLight, skyLight } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { H3, Text } from '../../../ui-library/typography/typography';
import { LineChartTooltip } from '../../process-analysis/components/line-chart/line-chart-tooltip';
import { fmtMetric } from '../model-comparison/model-comparison.helpers';
import { ADOPTION_TIERS } from './adoption.type';

type Props = {
  response: AgentAdoptionResponse;
};

/** Minimum pixel height of one row in the per-team composition chart. */
const TEAM_ROW_HEIGHT = 36;

/**
 * One row of the per-team composition chart: the tier percentages plus the
 * team name for the axis.
 */
type CompositionRow = {
  name: string;
  [tierKey: string]: string | number;
};

/**
 * "How sessions map to teams": the org-level tier breakdown as summary cards,
 * a per-team 100%-stacked composition chart, and the unattributed callout.
 * Every session counts once at org level (the cards sum to the session
 * total); the per-team bars show each team's own mix, where fan-out is fine
 * because each bar is normalized to its own team.
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} The attribution view content
 */
export const AdoptionAttributionView = ({ response }: Props) => {
  const { t } = useTranslation();
  const totalSessions = response.attribution_breakdown.total_sessions;

  const compositionRows = useMemo<CompositionRow[]>(() => {
    const allSessionsRow: CompositionRow = { name: t('agent_miner.adoption.all_sessions_row') };
    for (const tier of ADOPTION_TIERS) {
      allSessionsRow[tier.key] =
        totalSessions > 0 ? (response.attribution_breakdown.tiers[tier.key] / totalSessions) * 100 : 0;
    }
    const teamRows = response.teams
      .filter((team) => team.summary.sessions > 0)
      .map((team) => {
        const row: CompositionRow = { name: team.name };
        for (const tier of ADOPTION_TIERS) {
          row[tier.key] =
            tier.key === 'unattributed' ? 0 : (team.summary.attribution[tier.key] / team.summary.sessions) * 100;
        }
        return row;
      });
    return [allSessionsRow, ...teamRows];
  }, [response, t, totalSessions]);

  const tierShare = (count: number): number | null => (totalSessions > 0 ? (count / totalSessions) * 100 : null);

  return (
    <Stack gap={16} data-testid="adoption-attribution-view">
      <Stack gap={4}>
        <H3>{t('agent_miner.adoption.attribution_title')}</H3>
        <Text size="small" color={inkLight}>
          {t('agent_miner.adoption.attribution_subtitle', { total: totalSessions.toLocaleString() })}
        </Text>
      </Stack>
      <SimpleGrid cols={{ base: 2, lg: 5 }} spacing={16} data-testid="adoption-tier-cards">
        {ADOPTION_TIERS.map((tier) => (
          <SingleValueCard
            key={tier.key}
            title={
              <Group gap={4} wrap="nowrap">
                {t(tier.labelKey)}
                <Tooltip
                  label={t(tier.infoKey)}
                  position="top"
                  withArrow
                  multiline
                  maw={300}
                  events={{ hover: true, focus: true, touch: false }}
                >
                  <Box component="span" tabIndex={0} style={{ display: 'inline-flex', cursor: 'help' }}>
                    <Icon name="info" size={13} color={inkLight} />
                  </Box>
                </Tooltip>
              </Group>
            }
            icon={tier.icon}
            value={response.attribution_breakdown.tiers[tier.key].toLocaleString()}
            unit={`(${fmtMetric(tierShare(response.attribution_breakdown.tiers[tier.key]), '%')})`}
          />
        ))}
      </SimpleGrid>
      <ChartBox>
        <CompositionScroll>
          <ResponsiveContainer width="100%" height={Math.max(compositionRows.length * TEAM_ROW_HEIGHT + 60, 160)}>
            <BarChart data={compositionRows} layout="vertical" margin={{ top: 8, right: 16, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={skyLight} horizontal={false} />
              <XAxis
                type="number"
                domain={[0, 100]}
                tickFormatter={(value: number) => `${Math.round(value)}%`}
                tick={{ fontSize: 12 }}
                axisLine={{ stroke: skyLight }}
                tickLine={{ stroke: skyLight }}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={140}
                tick={{ fontSize: 12 }}
                axisLine={{ stroke: skyLight }}
                tickLine={false}
              />
              <RechartsTooltip
                content={<LineChartTooltip formatValue={(value: number | null) => fmtMetric(value, '%')} />}
              />
              {ADOPTION_TIERS.map((tier) => (
                <Bar
                  key={tier.key}
                  dataKey={tier.key}
                  name={t(tier.labelKey)}
                  stackId="tiers"
                  fill={tier.color}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </CompositionScroll>
        <Legend>
          {ADOPTION_TIERS.map((tier) => (
            <LegendItem key={tier.key}>
              <LegendSwatch style={{ backgroundColor: tier.color }} />
              <Text size="small" color={inkLight}>
                {t(tier.labelKey)}
              </Text>
            </LegendItem>
          ))}
        </Legend>
      </ChartBox>
      <UnattributedCallout data-testid="adoption-unattributed-callout">
        <Icon name="help_center" size={20} color={inkLight} />
        <Stack gap={2}>
          <Text size="small" weight="medium">
            {t('agent_miner.adoption.unattributed_summary', {
              sessions: response.unattributed_summary.total_sessions.toLocaleString(),
              users: response.unattributed_summary.active_ai_users.toLocaleString(),
              share: fmtMetric(response.summary.attribution_coverage.unattributed_rate, '%'),
            })}
          </Text>
          <Text size="small" color={inkLight}>
            {t('agent_miner.adoption.unattributed_hint')}
          </Text>
        </Stack>
      </UnattributedCallout>
    </Stack>
  );
};

/* Many-team orgs get a tall chart; scroll it inside the card instead of
   letting the whole main section stretch. */
const CompositionScroll = styled.div`
  max-height: 420px;
  overflow-y: auto;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  padding-top: 8px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LegendSwatch = styled.span`
  width: 11px;
  height: 11px;
  border-radius: 3px;
  flex-shrink: 0;
`;

const UnattributedCallout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border: 1px dashed ${skyLight};
  border-radius: 10px;
`;
